import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';

import { roleGridColumns } from './role-grid-columns';
import { UserPermissionName } from '../../../models/user-permission-name';
import { UserRoleDialog } from '../../offices/user-role/user-role.dialog';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { useLoadData } from '../../../hooks/useLoadData';
import { useSaveData } from '../../../hooks/useSaveData';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { ApiResponse } from '../../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
  headingStyle: { fontWeight: 'bold' },
};

interface UsersRoleComponent {
  name: string;
}

export const UsersRoleComponent = ({ name }: UsersRoleComponent) => {
  const { setValue, getValues } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenSelectServiceDialog = () => {
    setOpenDialog(true);
  };

  const handleSelectService = (users: any[]) => {
    setOpenDialog(false);
    setValue(name, users);
  };

  const handleCloseSelectServiceDialog = () => {
    setOpenDialog(false);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Customer', 'customerUsers'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUserList = (getValues(name) || []).filter((user: any) =>
      Object.values(user).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUsers(filteredUserList || []);
  }, [debouncedSearchTerm, getValues(name)]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'customerUsers',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'customerUsers',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      //   if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      //   }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            USERS
          </Typography>
        }
        action={
          <Button className="btnLink" variant="text" sx={{ p: 0, m: 0, mr: 1 }} onClick={handleOpenSelectServiceDialog}>
            Add User
          </Button>
        }
      />
      <CardContent>
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SiTrackGrid
            fileTitle='customer_user'
              dataItemKey="userID"
              columns={roleGridColumns}
              //   dataItems={getValues(name)}
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              // -----------------Grid Layout-------
              dataItems={filteredUsers || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
        </Grid>
        {openDialog && (
          <UserRoleDialog
            name={name}
            open={openDialog}
            onSelectedUsers={handleSelectService}
            onClose={handleCloseSelectServiceDialog}
          />
        )}
      </CardContent>
    </Card>
  );
};
